import React, { useMemo, useState } from 'react'
import { RefreshCw01 } from '@untitled-ui/icons-react'
import { MyConfirmModalWithChildren, MyDropzone, MyButton, myToaster } from '@interstellar-component'
import { format } from 'date-fns'
import Service from '../service'

// Re-upload the HR-supplied e-KTP after a `ktp_unclear` reject (enquiry sits at
// form_revision). The identity was already entered/validated at New Enquiry —
// the reject was about the PHOTO — so this only asks HR for a clearer image and
// re-sends the existing identity (incl. the bureau city_code) to /:id/ktp, which
// re-sets kyc.status='ktp_confirmed' so the candidate can pass face verification.

// Normalize the identity DOB (BE DATEONLY 'YYYY-MM-DD' or ISO) → 'yyyy-MM-dd'.
function toDobString(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  try {
    return format(new Date(s), 'yyyy-MM-dd')
  } catch {
    return ''
  }
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="shrink-0 text-sm text-gray-500">{label}</span>
      <span className="max-w-[65%] text-right text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
        {value || '-'}
      </span>
    </div>
  )
}

export default function ReuploadEktpModal({
  open,
  onClose,
  zIndex = 3500,
  enquiryId,
  identity = {},
  whatsappNumber,
  onSuccess,
}) {
  const [photo, setPhoto] = useState([])
  const [photoError, setPhotoError] = useState('')
  const [loading, setLoading] = useState(false)

  const dob = useMemo(() => toDobString(identity.date_of_birth), [identity])

  // The five KTP fields the BE requires — an HR-supplied enquiry always has
  // them; guard anyway so a malformed record surfaces a clear message instead
  // of a raw 422 on submit.
  const missingRequired = useMemo(() => {
    const need = {
      NIK: identity.nik,
      Name: identity.full_name,
      'Date of birth': dob,
      Gender: identity.gender,
      Address: identity.address,
    }
    return Object.entries(need)
      .filter(([, v]) => !v)
      .map(([k]) => k)
  }, [identity, dob])

  const genderLabel =
    identity.gender === 'male' ? 'Male' : identity.gender === 'female' ? 'Female' : '-'

  const handleSubmit = async () => {
    const file = Array.isArray(photo) ? photo[0] : null
    if (!file) {
      setPhotoError('A clearer KTP photo is required.')
      return
    }
    if (missingRequired.length) {
      myToaster({
        status: 422,
        message: `Cannot re-upload — missing identity data: ${missingRequired.join(', ')}. Cancel this enquiry and create a new one.`,
      })
      return
    }
    setPhotoError('')
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('ktp_number', identity.nik)
      fd.append('ktp_name', identity.full_name)
      fd.append('ktp_date_of_birth', dob)
      fd.append('ktp_gender', identity.gender)
      fd.append('ktp_address', identity.address)
      // Optional bureau region fields — re-send so CLIK keeps the address.
      fd.append('ktp_city', identity.city ?? '')
      fd.append('ktp_city_code', identity.city_code ?? '')
      fd.append('ktp_district', identity.district ?? '')
      fd.append('ktp_village', identity.subdistrict ?? '')
      fd.append('ktp_postal_code', identity.postal_code ?? '')
      if (whatsappNumber && whatsappNumber !== '-') {
        fd.append('whatsapp_number', whatsappNumber)
      }

      const res = await Service.uploadKtp(enquiryId, fd)
      myToaster(res)
      setPhoto([])
      onSuccess?.()
      onClose?.()
    } catch (err) {
      myToaster(err)
    } finally {
      setLoading(false)
    }
  }

  const footerActions = (
    <div className="mt-6 mb-6 flex w-full gap-3">
      <MyButton
        expanded
        color="secondary"
        variant="outlined"
        size="lg"
        onClick={onClose}
        disabled={loading}
      >
        Cancel
      </MyButton>
      <MyButton
        expanded
        color="secondary"
        variant="outlined"
        size="lg"
        onClick={handleSubmit}
        disabled={loading}
        customClassname="!border-brand/300 !text-brand/700 hover:!bg-brand/50"
      >
        {loading ? 'Uploading...' : 'Re-upload e-KTP'}
      </MyButton>
    </div>
  )

  return (
    <MyConfirmModalWithChildren
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      forceBlur
      title="Re-upload e-KTP"
      message="Upload a clearer e-KTP photo. The identity data on file is reused — edit the enquiry only if the data itself is wrong."
      icon={<RefreshCw01 className="text-brand/900" />}
      bgColor="bg-white"
      disableBgPattern
      enableDoubleRing
      footerClassName="!px-0 !mt-0 !mb-0"
      positiveActionWord={footerActions}
    >
      <div className="flex flex-col gap-6">
        {/* New KTP photo */}
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium text-gray-700">
            New KTP photo<span className="ml-0.5 text-brand/900">*</span>
          </p>
          <MyDropzone
            accept={['.jpg', '.jpeg', '.png', '.webp']}
            maxSize={5 * 1024 * 1024}
            multiple={false}
            showImage
            onChange={(files) => {
              setPhoto(files)
              if (photoError) setPhotoError('')
            }}
          />
          {photoError && <p className="text-sm text-error/600">{photoError}</p>}
        </div>

        {/* Identity on file (read-only) */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2">
          <p className="py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Identity on file
          </p>
          <div className="divide-y divide-gray-100">
            <SummaryRow label="NIK" value={identity.nik} />
            <SummaryRow label="Name" value={identity.full_name} />
            <SummaryRow
              label="Date of birth"
              value={dob ? format(new Date(`${dob}T00:00:00`), 'MMM d, yyyy') : '-'}
            />
            <SummaryRow label="Gender" value={genderLabel} />
            <SummaryRow label="City" value={identity.city} />
            <SummaryRow label="District" value={identity.district} />
            <SummaryRow label="Subdistrict" value={identity.subdistrict} />
            <SummaryRow label="Postal code" value={identity.postal_code} />
            <SummaryRow label="Address" value={identity.address} />
          </div>
        </div>

        {missingRequired.length > 0 && (
          <p className="text-sm text-error/600">
            Missing identity data ({missingRequired.join(', ')}). This enquiry
            can&apos;t be re-uploaded — cancel it and create a new one.
          </p>
        )}
      </div>
    </MyConfirmModalWithChildren>
  )
}
